import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";

import {
  createPluginConnections,
  type AuthorizationWindow,
  type PluginConnectionRequestOutcome,
  type PluginConnections,
} from "./pluginConnections";
import {
  beginPluginAuthorization,
  disconnectPluginConnection,
  fetchPlugins,
} from "./pluginsPage.api";
import type { ForgePluginId } from "./pluginsPage.logic";

const PluginConnectionsContext = createContext<PluginConnections | null>(null);

function openAuthorizationWindow(): AuthorizationWindow | null {
  const popup = window.open("", "_blank");
  if (popup === null) return null;
  return {
    get closed() {
      return popup.closed;
    },
    close: () => popup.close(),
    navigate: (url) => {
      popup.location.href = url;
    },
  };
}

function createBrowserPluginConnections(): PluginConnections {
  return createPluginConnections({
    load: fetchPlugins,
    beginAuthorization: beginPluginAuthorization,
    disconnect: disconnectPluginConnection,
    openAuthorizationWindow,
    schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
    cancelSchedule: (scheduleId) => window.clearTimeout(scheduleId),
    now: Date.now,
  });
}

export function PluginConnectionsProvider({ children }: { readonly children: ReactNode }) {
  const [connections] = useState(createBrowserPluginConnections);

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { readonly type?: unknown; readonly status?: unknown } | null;
      if (payload?.type !== "forge-plugin-oauth-complete" || payload.status !== "connected") return;
      void connections.reconcile();
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      connections.dispose();
    };
  }, [connections]);

  return (
    <PluginConnectionsContext.Provider value={connections}>
      {children}
    </PluginConnectionsContext.Provider>
  );
}

export interface UsePluginConnectionsOptions {
  readonly enabled?: boolean;
}

export function usePluginConnections(options: UsePluginConnectionsOptions = {}) {
  const connections = useContext(PluginConnectionsContext);
  if (connections === null) {
    throw new Error("usePluginConnections must be used inside PluginConnectionsProvider");
  }
  const snapshot = useSyncExternalStore(connections.subscribe, connections.read, connections.read);
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    void connections.reconcile();
  }, [connections, enabled]);

  return {
    plugins: snapshot.plugins,
    loading: snapshot.phase === "loading",
    initialized: snapshot.revision > 0,
    error: snapshot.failure,
    attempts: snapshot.attempts,
    refresh: connections.reconcile,
    connect: (pluginId: ForgePluginId): Promise<PluginConnectionRequestOutcome> =>
      connections.request({ action: "connect", pluginId }),
    disconnect: (pluginId: ForgePluginId): Promise<PluginConnectionRequestOutcome> =>
      connections.request({ action: "disconnect", pluginId }),
  };
}
